import {
  descargarPlantilla, leerLibro, buscarTabla, vacio, texto, sinDato, elegirDeLista,
  normalizarDireccion, momentoDe, leerMomento, ejemplosDirecciones, fechaDeMomento,
} from "./importar_masivo";
import {
  nuevaEncomienda, rutaColeccionEncomiendas, viviendaCorreo, viviendaNotificacion, bitacoraCreacion, bitacoraGestion,
} from "./encomienda";
import { fechaDart, fechaHoraDart, fechaHora12Dart } from "./pase_visitante";
import { consultarSimple, generarId } from "./crud";

// Carga masiva de encomiendas desde Excel o CSV (solo web).
// La plantilla tiene las mismas columnas y en el mismo orden que la tabla del listado,
// así que el archivo que se exporta del listado también se puede volver a cargar.

export const ESTADOS_CARGA = ["Pendiente", "Recibido", "Entregado", "Anulado"];

export const COLUMNAS_PLANTILLA = [
  "FECHA CREACIÓN", "DIRECCIÓN", "REGISTRADO POR", "QUIÉN LO TRAE",
  "DESCRIPCIÓN", "ESTADO", "RECIBE", "FECHA ENTREGA",
];

export const EJEMPLOS_PLANTILLA = [
  ["07/10/2026 09:38", "TORRE 1 APARTAMENTO 302", "RECEPCIÓN", "SERVIENTREGA", "Caja mediana de cartón", "Recibido", "", ""],
  ["07/10/2026 09:27", "MANZANA C CASA 2", "RECEPCIÓN", "MERCADO LIBRE", "Sobre blanco con documentos", "Entregado", "Ana Pérez", "07/10/2026 11:40"],
  ["", "CASA 15", "", "RAPPI", "Bolsa de mercado", "", "", ""],
];

export const descargarPlantillaEncomiendas = () => descargarPlantilla({
  archivo: "plantilla_encomiendas.xlsx",
  hoja: "Encomiendas",
  columnas: COLUMNAS_PLANTILLA,
  ejemplos: EJEMPLOS_PLANTILLA,
  anchos: [18, 28, 18, 18, 30, 12, 18, 18],
  instrucciones: [
    ["FECHA CREACIÓN", "No", "Día y hora en que llegó el paquete, como 07/10/2026 09:38 (día/mes/año y hora de 24 horas). Si la deja vacía se usa la fecha y hora de la carga."],
    ["DIRECCIÓN", "Sí", "Igual que en CoordiPH: TORRE 1 APARTAMENTO 302, MANZANA C CASA 2, CASA 15, LOCAL 1, ADMINISTRACIÓN o PORTERIA."],
    ["REGISTRADO POR", "No", "Quién recibió el paquete en portería. Si la deja vacía se usa el nombre de quien hace la carga."],
    ["QUIÉN LO TRAE", "Sí", "Empresa o persona que entrega el paquete. Ejemplo: SERVIENTREGA."],
    ["DESCRIPCIÓN", "Sí", "Cómo es el paquete. Ejemplo: Caja mediana de cartón."],
    ["ESTADO", "No", "Pendiente, Recibido, Entregado o Anulado. Si la deja vacía queda Recibido (o Entregado si escribió la fecha de entrega)."],
    ["RECIBE", "Solo si está Entregado", "Nombre de la persona que recogió el paquete."],
    ["FECHA ENTREGA", "Solo si está Entregado", "Día y hora en que se entregó, como 07/10/2026 11:40. No puede ser antes de la fecha de creación."],
  ],
});

// El orden importa: la primera regla que coincide decide el campo.
const REGLAS_COLUMNAS = [
  ["horaEntrega",  /\bhora\b.*\bentrega/],
  ["fechaEntrega", /\bentrega/],
  ["hora",         /\bhora\b/],
  ["fecha",        /\bfecha\b/],
  ["estado",       /\bestado\b/],
  ["recibe",       /\b(recibe|recibio|recibido por|reclama|reclamo)\b/],
  ["usuario",      /\b(registrado|registro|registra|usuario|portero|guarda)\b/],
  ["quienLoTrae",  /\b(quien|trae|empresa|transportadora|remitente|mensajeria|envia)\b/],
  ["descripcion",  /\b(descripcion|paquete|contenido|detalle|observacion|observaciones)\b/],
  ["vivienda",     /\b(direccion|vivienda|inmueble|apartamento|apto|casa|destinatario|unidad)\b/],
];

const esEncabezado = (c) => c.vivienda !== undefined && (c.quienLoTrae !== undefined || c.descripcion !== undefined);

// ahora: fecha y hora de la carga (se usa cuando la fila no trae fecha u hora).
// Devuelve { encontroEncabezado, registros, errores, ejemplos }.
// Cada registro: { fila, original, vivienda, usuario, quienLoTrae, descripcion, estado, recibe,
//   fecha: "aaaa-mm-dd", hora: "HH:mm", conFecha, fechaEntrega, horaEntrega }.
export const leerLibroEncomiendas = (libro, ahora = new Date()) => {
  const tabla = buscarTabla(libro, REGLAS_COLUMNAS, esEncabezado);
  if (!tabla) return { encontroEncabezado: false, registros: [], errores: [], ejemplos: [] };

  const { fecha: hoy, hora: horaActual } = momentoDe(ahora);
  const momentoActual = `${hoy} ${horaActual}`;
  const { filas, indice, columnas } = tabla;
  const celda = (fila, campo) => (columnas[campo] !== undefined ? fila[columnas[campo]] : "");
  const registros = [];
  const errores = [];

  filas.slice(indice + 1).forEach((fila, i) => {
    const numeroFila = indice + i + 2;
    if (fila.every(vacio)) return;

    const original = texto(celda(fila, "vivienda"));
    const quienLoTrae = texto(celda(fila, "quienLoTrae"));
    const descripcion = String(celda(fila, "descripcion")).trim();
    const usuario = texto(celda(fila, "usuario"));
    const recibe = sinDato(celda(fila, "recibe")) ? "" : texto(celda(fila, "recibe"));
    const valorEstado = texto(celda(fila, "estado"));

    const problemas = [];
    const faltan = [];
    if (!original) faltan.push("la dirección");
    if (!quienLoTrae) faltan.push("quién lo trae");
    if (!descripcion) faltan.push("la descripción");
    if (faltan.length) problemas.push(`falta ${faltan.join(", ")}`);

    const creacion = leerMomento(celda(fila, "fecha"), celda(fila, "hora"), "de creación", problemas);
    const entrega = leerMomento(celda(fila, "fechaEntrega"), celda(fila, "horaEntrega"), "de entrega", problemas);

    let estado = elegirDeLista(valorEstado, ESTADOS_CARGA);
    if (valorEstado && !estado) problemas.push(`el estado "${valorEstado}" no existe; use ${ESTADOS_CARGA.join(", ")}`);
    if (!valorEstado) estado = entrega ? "Entregado" : "Recibido";

    const fecha = creacion?.fecha || hoy;
    const hora = creacion?.hora || (creacion ? "00:00" : horaActual);
    if (`${fecha} ${hora}` > momentoActual) problemas.push("la fecha de creación es posterior a hoy");

    let fechaEntrega = null;
    let horaEntrega = null;
    if (estado === "Entregado") {
      if (!recibe) problemas.push("está Entregado pero falta quién lo recibe");
      if (!entrega && !problemas.some(p => p.includes("de entrega"))) problemas.push("está Entregado pero falta la fecha de entrega");
      if (entrega) {
        fechaEntrega = entrega.fecha;
        horaEntrega = entrega.hora || hora;
        const momentoEntrega = `${fechaEntrega} ${horaEntrega}`;
        if (momentoEntrega < `${fecha} ${hora}`) problemas.push("la fecha de entrega es anterior a la de creación");
        else if (momentoEntrega > momentoActual) problemas.push("la fecha de entrega es posterior a hoy");
      }
    }

    if (problemas.length) {
      errores.push({ fila: numeroFila, texto: problemas.join("; ") });
      return;
    }

    registros.push({
      fila: numeroFila,
      original,
      vivienda: normalizarDireccion(original),
      usuario,
      quienLoTrae,
      descripcion,
      estado,
      recibe: estado === "Entregado" ? recibe : "",
      fecha,
      hora,
      conFecha: Boolean(creacion),
      fechaEntrega,
      horaEntrega,
    });
  });

  return { encontroEncabezado: true, registros, errores, ejemplos: ejemplosDirecciones(registros) };
};

export const leerArchivoEncomiendas = async (archivo, ahora = new Date()) =>
  leerLibroEncomiendas(await leerLibro(archivo), ahora);

// ---------- Guardado ----------

// Solo los paquetes recibidos hoy y aún sin entregar generan aviso en la app.
export const recibeAvisoHoy = (r) => r.fecha === fechaDart() && r.estado === "Recibido";

// Contexto extra para guardar: el correo de aviso de cada vivienda.
export const prepararCargaEncomiendas = async (nit) => ({ correos: (await consultarSimple(`notificaciones/${nit}`)) || {} });

// Rutas y valores de Firebase de una encomienda leída del archivo (mismos campos que crearla a mano).
export const cambiosEncomiendaCargada = (r, i, { nit, usuario, ahora, correos }) => {
  const fechaCreacion = fechaHoraDart(fechaDeMomento(r, i));
  const ruta = rutaColeccionEncomiendas(nit, fechaCreacion.substring(0, 4), fechaCreacion.substring(5, 7));
  const id = generarId(ruta);
  const correo = correos[viviendaCorreo(r.vivienda)];
  const entregado = r.estado === "Entregado";
  const bitacora = bitacoraCreacion(usuario, fechaDart(ahora));

  const cambios = {
    [`${ruta}/${id}`]: nuevaEncomienda({
      fechaCreacion,
      vivienda: r.vivienda,
      usuario: r.usuario || usuario,
      descripcionDomicilio: r.descripcion,
      estado: r.estado,
      quienLoTrae: r.quienLoTrae,
      fecha_inicio_encomienda: fechaCreacion.substring(0, 10),
      hora_inicio_encomienda: fechaCreacion.substring(11, 16),
      descripcion_encomienda: entregado ? bitacoraGestion(bitacora, usuario, fechaHora12Dart(ahora)) : bitacora,
      correoNotificacion: typeof correo === "string" ? correo : null,
      ...(entregado ? {
        nombre_completo_persona_recibe_paquete: r.recibe,
        fecha_fin_encomienda: r.fechaEntrega,
        hora_fin_encomienda: r.horaEntrega,
      } : {}),
    }),
  };
  if (recibeAvisoHoy(r)) {
    cambios[`coordi_ph_clientes/${nit}/fcm_encomiendas/recibido/${viviendaNotificacion(r.vivienda)}`] = { id, fecha: fechaCreacion };
  }
  return cambios;
};
